import { MilestoneStatus } from '@thoughtspot/blink-context';
import { AgentId, SaveIdToAgentIdMapping } from '@thoughtspot/sparkflow';
import { getOnboardingLocationSelected } from '/@services/system/config-service/session-service';
import { ChecklistItem } from './checklist-modal/checklist-modal.types';
import { isSectionComplete } from './trial-info.util';
import {
    AGENT_SELECTION_MILESTONE_CONFIG,
    AgentMilestoneConfig,
    EXPLORE_DASHBOARDS_SECTION_ID,
    MILESTONE_SECOND_SECTION_BY_AGENT,
} from './trial-info-sparkflow.constants';

export interface MilestoneDecision {
    showMilestoneModal: boolean;
    milestoneConfig: AgentMilestoneConfig | null;
    milestoneStatus: MilestoneStatus;
}

export const getMilestoneDecision = (
    wasSectionCompleted: boolean,
    sparkflowInsightsData: ChecklistItem[] | undefined,
    updatedCheckedItems: Set<string>,
    shownStarted: boolean,
    shownFinished: boolean,
): MilestoneDecision => {
    const agentId: AgentId | null =
        SaveIdToAgentIdMapping[getOnboardingLocationSelected()] ?? null;

    const milestoneConfig =
        (wasSectionCompleted &&
            agentId &&
            AGENT_SELECTION_MILESTONE_CONFIG[agentId]) ||
        null;

    const secondSectionId =
        (agentId && MILESTONE_SECOND_SECTION_BY_AGENT[agentId]) ??
        EXPLORE_DASHBOARDS_SECTION_ID;

    const { agentSection, secondSection } = milestoneConfig
        ? (sparkflowInsightsData ?? []).reduce<{
              agentSection: ChecklistItem | undefined;
              secondSection: ChecklistItem | undefined;
          }>(
              (acc, s) => ({
                  agentSection:
                      acc.agentSection ??
                      (s.id === milestoneConfig.sectionId ? s : undefined),
                  secondSection:
                      acc.secondSection ??
                      (s.id === secondSectionId ? s : undefined),
              }),
              { agentSection: undefined, secondSection: undefined },
          )
        : { agentSection: undefined, secondSection: undefined };

    const isAgentSectionComplete = !!(
        agentSection && isSectionComplete(agentSection, updatedCheckedItems)
    );
    /* Treat missing section as complete so it never blocks the modal
     * in journeys that don't have that section. */
    const isSecondSectionComplete =
        !secondSection || isSectionComplete(secondSection, updatedCheckedItems);

    const milestoneStatus =
        isAgentSectionComplete && isSecondSectionComplete
            ? MilestoneStatus.FINISHED
            : MilestoneStatus.STARTED;

    /* FINISHED is blocked until STARTED has been shown, preventing M2
     * from skipping M1 when the secondary section completed first. */
    const alreadyShown =
        milestoneStatus === MilestoneStatus.FINISHED
            ? !shownStarted || shownFinished
            : shownStarted;

    const showMilestoneModal = isAgentSectionComplete && !alreadyShown;

    return {
        showMilestoneModal,
        milestoneConfig: showMilestoneModal ? milestoneConfig : null,
        milestoneStatus,
    };
};
